import React from "react";
import { ArrowUpRight } from "lucide-react";

// Short product labels for the lightweight architecture diagram.
// Ten — matches the product grid. KOVA appears both here and at the mid-tier;
// it is both a product (#05) and the integration mesh that feeds MATR.
const PRODUCT_LABELS = [
  "Ryan",
  "Fol.io",
  "Uday",
  "EULR",
  "KOVA",
  "TRNSLT",
  "Cortex",
  "PTRN",
  "Railwright",
  "Vernai",
];

/**
 * Lightweight three-tier signal-flow diagram.
 *
 * Products row (bottom) → KOVA (mid) → MATR (top)
 *
 * Two simple connectors. One subtle pulsing dot travels up each connector on a
 * slow loop. Intentionally NOT ten fan-out lines — that level of detail belongs
 * to the product grid in §02, not here. Stays inside the natural scroll.
 */
const ArchitectureDiagram = () => {
  return (
    <div
      data-testid="matr-architecture-diagram"
      className="mt-12 mb-2 mx-auto max-w-[520px] select-none"
      aria-label="MERA architecture: ten products feed KOVA, KOVA feeds MATR"
      role="img"
    >
      {/* MATR — top tier */}
      <div className="flex justify-center">
        <div className="px-6 py-2.5 border border-white/30 rounded-full bg-white/[0.04]">
          <span className="font-serif-display text-[22px] tracking-tight text-white">
            MATR
          </span>
        </div>
      </div>

      {/* Connector: KOVA → MATR */}
      <div className="relative mx-auto w-px h-12 bg-white/15 my-3">
        <span className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white/85 architecture-dot architecture-dot--top" />
      </div>

      {/* KOVA — mid tier */}
      <div className="flex justify-center">
        <div className="px-5 py-2 border border-white/20 rounded-full bg-white/[0.025]">
          <span className="font-serif-display text-[16px] tracking-tight text-white/85">
            KOVA
          </span>
          <span className="ml-2 font-mono-label text-[9px] text-white/45">
            Integration mesh
          </span>
        </div>
      </div>

      {/* Connector: Products → KOVA */}
      <div className="relative mx-auto w-px h-12 bg-white/15 my-3">
        <span className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-white/70 architecture-dot architecture-dot--bottom" />
      </div>

      {/* Counter — anchors the "complete ecosystem" story */}
      <div
        data-testid="matr-product-counter"
        className="flex items-center justify-center gap-2 mb-3"
      >
        <span aria-hidden className="inline-block w-6 h-px bg-white/20" />
        <span className="font-mono-label text-[9.5px] tracking-[0.22em] text-white/55">
          <span className="text-white/85 tabular-nums">10</span>
          <span className="text-white/30 mx-1">/</span>
          <span className="text-white/40 tabular-nums">10</span>
          <span className="ml-2 text-white/40">instruments</span>
        </span>
        <span aria-hidden className="inline-block w-6 h-px bg-white/20" />
      </div>

      {/* Products — bottom tier (10 small labels) */}
      <div className="flex flex-wrap justify-center gap-x-2 gap-y-1.5 px-2">
        {PRODUCT_LABELS.map((p) => (
          <span
            key={p}
            className="font-mono-label text-[9.5px] text-white/55 px-2 py-1 border border-white/10 rounded-full whitespace-nowrap"
          >
            {p}
          </span>
        ))}
      </div>

      {/* Legend */}
      <div className="mt-5 font-mono-label text-[9px] text-white/30 text-center tracking-[0.22em]">
        Ten products · one mesh · one intelligence layer
      </div>
    </div>
  );
};

export const Matr = () => {
  return (
    <section
      id="matr"
      data-testid="matr-section"
      className="relative px-6 md:px-10 py-28 md:py-36 bg-[#0A0A0A] text-[#F7F7F6] overflow-hidden"
    >
      {/* Decorative oversized mark */}
      <div
        aria-hidden
        className="absolute -top-16 right-[-6%] font-serif-display text-[40vw] md:text-[26vw] leading-none text-white/[0.04] select-none pointer-events-none float-slow"
      >
        M
      </div>

      <div className="grid grid-cols-12 gap-6 relative">
        <div className="col-span-12 md:col-span-3">
          <div className="font-mono-label text-[11px] text-white/55 sticky top-32">
            § 03.5 · The Intelligence Layer
          </div>
        </div>

        <div className="col-span-12 md:col-span-9">
          {/* New transition line — names the two tracks explicitly */}
          <p
            data-testid="matr-transition"
            className="font-serif-display italic text-[22px] md:text-[28px] leading-[1.35] text-white/90 max-w-[760px] mb-10"
          >
            Most people use one MERA product. Some need the whole architecture
            running for them. MATR is how that works.
          </p>

          <div className="flex items-center gap-3 mb-8">
            <span className="inline-block w-8 h-px bg-white/40" />
            <span
              data-testid="matr-eyebrow"
              className="font-mono-label text-[11px] text-white/70"
            >
              MATR · Human Intelligence Platform
            </span>
          </div>

          <h2
            data-testid="matr-headline"
            className="font-serif-display text-[44px] md:text-[88px] leading-[0.98] tracking-tight"
          >
            One layer beneath{" "}
            <span className="italic-accent text-white/85">everything.</span>
          </h2>

          {/* Lightweight architecture visual — sits between headline and prose */}
          <ArchitectureDiagram />

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 max-w-[1100px]">
            <p className="text-[16px] md:text-[17px] leading-relaxed text-white/72">
              MATR is the Human Intelligence Platform at the center of the MERA
              ecosystem. Every interaction across Ryan, EULR, Fol.io, KOVA,
              Uday, Cortex, PTRN, Railwright, and Vernai feeds MATR. It learns
              how specific humans work, communicate, decide, and grow — and
              returns amplified intelligence back to every product in the
              ecosystem.
            </p>

            <p className="text-[16px] md:text-[17px] leading-relaxed text-white/72">
              MATR charges per human outcome, not per seat. The more humans in
              the ecosystem, the smarter every product becomes — for every
              human in it.
            </p>
          </div>

          <p
            data-testid="matr-closing"
            className="mt-14 max-w-[820px] font-serif-display italic text-[22px] md:text-[30px] leading-[1.3] text-white/90"
          >
            Every platform ever built organized intelligence around the
            business. MATR organizes intelligence around the human.
          </p>

          <div className="mt-14 flex flex-wrap items-center gap-6">
            <a
              href="#waitlist"
              data-testid="matr-cta"
              className="mera-btn"
              style={{
                background: "#F7F7F6",
                color: "#0A0A0A",
                borderColor: "#F7F7F6",
              }}
            >
              <span className="dot" />
              Learn about MATR
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </a>
            <div className="font-mono-label text-[10px] text-white/45">
              The architecture that makes the ecosystem greater than the sum of
              its parts.
            </div>
          </div>
        </div>
      </div>

      {/* Bottom edge marker */}
      <div className="mt-20 pt-6 border-t border-white/10 grid grid-cols-2 md:grid-cols-5 gap-y-3">
        {[
          "01 / Memory",
          "02 / Commerce",
          "03 / Intelligence",
          "04 / Governance",
          "05 / Mastery",
        ].map((t) => (
          <div
            key={t}
            className="font-mono-label text-[10px] text-white/45"
          >
            ◦ {t}
          </div>
        ))}
      </div>
    </section>
  );
};
